import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { SCHOOL_NAV } from "./SchoolDashboard";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useRfqBasket } from "@/context/RfqBasketContext";
import {
  BookOpen, PenTool, Shirt, Footprints, Trophy, FlaskConical,
  Printer, FileSpreadsheet, Search, Plus, Minus, X, ChevronLeft, Send,
} from "lucide-react";

/* 8 school-facing categories. Prices are NEVER shown on this page. */
const CATEGORIES = [
  { key: "Library Books",              icon: BookOpen,      img: "/categories/library-academic-books.jpg" },
  { key: "Academic Books",             icon: BookOpen,      img: "/categories/library-academic-books.jpg" },
  { key: "Stationery",                 icon: PenTool,       img: "/categories/stationery-office-supplies.jpg" },
  { key: "Uniforms",                   icon: Shirt,         img: "/categories/uniforms-accessories.jpg" },
  { key: "Shoes",                      icon: Footprints,    img: "/categories/school-shoes.jpg" },
  { key: "Sports Equipment",           icon: Trophy,        img: "/categories/sports-physical-education.jpg" },
  { key: "Laboratory Supplies",        icon: FlaskConical,  img: "/categories/science-lab-supplies.jpg" },
  { key: "Printing & Branding Services", icon: Printer,     img: "/categories/it-smart-classroom.jpg" },
];

export default function PlaceOrder() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [active, setActive]     = useState(null);
  const [q, setQ]               = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const basket = useRfqBasket();
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        // API strips ALL commercial fields for school_admin — no price/margin/etc.
        const { data } = await api.get("/products");
        setProducts(data || []);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally { setLoading(false); }
    })();
  }, []);

  const catCount = useMemo(() => {
    const m = {};
    for (const p of products) {
      const c = p.category || "Uncategorised";
      m[c] = (m[c] || 0) + 1;
    }
    return m;
  }, [products]);

  const filtered = useMemo(() => {
    if (!active) return [];
    const ql = q.trim().toLowerCase();
    return products.filter((p) => {
      const cat = (p.category || "").toLowerCase();
      const tileKey = active.toLowerCase();
      const matchCat = cat.includes(tileKey.split(" ")[0]) ||
                       tileKey.includes(cat.split(" ")[0] || "");
      if (!matchCat && p.category !== active) return false;
      if (!ql) return true;
      return (p.name || "").toLowerCase().includes(ql) ||
             (p.description || "").toLowerCase().includes(ql) ||
             (p.class_level || "").toString().toLowerCase().includes(ql);
    });
  }, [active, q, products]);

  return (
    <DashboardLayout nav={SCHOOL_NAV} title="Request Quotation">
      {/* ── Info strip ────────────────────────────────────── */}
      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 mb-4 text-sm text-blue-900 flex items-start gap-3">
        <FileSpreadsheet size={18} className="mt-0.5 flex-shrink-0" />
        <div>
          <div className="font-semibold">School procurement runs through Request for Quotation.</div>
          <div className="text-blue-800/90 mt-1">
            Add the items your school needs to a basket. Our team will respond with a
            formal quotation, timelines and delivery options. Prices are shared only
            once a quote is issued.
          </div>
        </div>
      </div>

      {/* ── Top strip: back + search + basket ─────────────── */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-6">
        {active && (
          <button
            data-testid="btn-back-categories"
            onClick={() => { setActive(null); setQ(""); }}
            className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-brand-blue"
          >
            <ChevronLeft size={16} /> All categories
          </button>
        )}
        {active && (
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-3 text-slate-400" />
            <input
              data-testid="input-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Search in ${active}…`}
              className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-300 focus:outline-none focus:border-brand-blue text-sm"
            />
          </div>
        )}
        <button
          data-testid="btn-rfq-basket"
          onClick={() => setDrawerOpen(true)}
          className="ml-auto inline-flex items-center gap-2 px-3 py-2 rounded-md bg-brand-blue text-white font-semibold hover:bg-brand-blueDark"
        >
          <FileSpreadsheet size={16} />
          RFQ Basket {basket.totalUnits > 0 && (
            <span className="bg-white text-brand-blue text-xs font-bold rounded-full min-w-[20px] h-5 grid place-items-center px-1.5">
              {basket.totalUnits}
            </span>
          )}
        </button>
      </div>

      {/* ── Category grid ─────────────────────────────────── */}
      {!active && (
        <>
          <h2 className="text-2xl font-heading font-semibold text-brand-navy mb-6">
            What does your school need today?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                data-testid={`cat-${c.key.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,'')}`}
                onClick={() => setActive(c.key)}
                className="group text-left rounded-2xl overflow-hidden border border-slate-200 bg-white hover:shadow-lg hover:border-brand-blue/40 transition-all"
              >
                <div className="h-32 overflow-hidden relative">
                  <img src={c.img} alt={c.key} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/60 to-transparent" />
                  <div className="absolute top-3 left-3 w-9 h-9 rounded-lg bg-white/90 grid place-items-center text-brand-blue">
                    <c.icon size={18} />
                  </div>
                </div>
                <div className="p-4">
                  <div className="font-heading font-semibold text-brand-navy">{c.key}</div>
                  <div className="text-xs text-slate-500 mt-1">{catCount[c.key] || 0} items available</div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {/* ── Product grid — NO PRICE ANYWHERE ──────────────── */}
      {active && (
        <>
          <div className="text-sm text-slate-500 mb-3">{filtered.length} items in {active}</div>
          {loading ? (
            <div className="text-slate-500">Loading products…</div>
          ) : filtered.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-500">
              No items match your search. Try a different keyword.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filtered.map((p) => <ProductCard key={p.id} p={p} />)}
            </div>
          )}
        </>
      )}

      {drawerOpen && <RfqDrawer close={() => setDrawerOpen(false)} onSubmitted={() => nav("/school/rfq-history")} />}
    </DashboardLayout>
  );
}

function ProductCard({ p }) {
  const basket = useRfqBasket();
  const line = basket.items.find((x) => x.product_id === p.id);
  const comingSoon = p.is_live === false;
  return (
    <div data-testid={`product-${p.id}`} className="rounded-xl overflow-hidden border border-slate-200 bg-white flex flex-col relative">
      <div className="h-40 bg-white flex items-center justify-center overflow-hidden relative border-b border-slate-100">
        {p.image ? (
          <img src={p.image} alt={p.name}
               className={`max-h-full max-w-full object-contain ${comingSoon ? "opacity-60 grayscale" : ""}`}
               loading="lazy"
               onError={(e) => { e.currentTarget.src = "/api/static/library-covers/cover-unavailable.webp"; }} />
        ) : (
          <div className="w-full h-full grid place-items-center text-slate-400 text-xs">No image</div>
        )}
        {comingSoon && (
          <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wider text-slate-700 bg-white/95 border border-slate-200 rounded-full px-2 py-0.5">
            Coming Soon
          </span>
        )}
      </div>
      <div className="p-3 flex-1 flex flex-col">
        <div className="font-semibold text-sm text-brand-navy line-clamp-2 min-h-[2.5rem]">{p.name}</div>
        <div className="text-xs text-slate-500 mt-1 line-clamp-2">{p.description || " "}</div>
        {/* NO PRICE DISPLAY */}
        <div className="mt-3">
          {comingSoon ? (
            <button data-testid={`btn-add-${p.id}`} disabled
                    onClick={() => toast.info("Coming Soon — this category will be available on SchoolLane shortly.")}
                    className="w-full inline-flex items-center justify-center gap-1 py-2 rounded-md bg-slate-200 text-slate-500 text-sm font-semibold cursor-not-allowed">
              Coming Soon
            </button>
          ) : !line ? (
            <button
              data-testid={`btn-add-${p.id}`}
              onClick={() => { basket.add(p); toast.success("Added to RFQ basket"); }}
              className="w-full inline-flex items-center justify-center gap-1 py-2 rounded-md bg-brand-green text-white text-sm font-semibold hover:bg-brand-greenDark"
            >
              <Plus size={14} /> Add to RFQ
            </button>
          ) : (
            <QtyStepper id={p.id} qty={line.qty} />
          )}
        </div>
      </div>
    </div>
  );
}

function QtyStepper({ id, qty }) {
  const basket = useRfqBasket();
  return (
    <div className="flex items-center justify-between border border-slate-300 rounded-md overflow-hidden">
      <button data-testid={`btn-dec-${id}`}
              onClick={() => qty <= 1 ? basket.remove(id) : basket.setQty(id, qty - 1)}
              className="p-2 hover:bg-slate-100">
        <Minus size={14} />
      </button>
      <span data-testid={`qty-${id}`} className="text-sm font-semibold">{qty}</span>
      <button data-testid={`btn-inc-${id}`}
              onClick={() => basket.setQty(id, qty + 1)}
              className="p-2 hover:bg-slate-100">
        <Plus size={14} />
      </button>
    </div>
  );
}

function RfqDrawer({ close, onSubmitted }) {
  const basket = useRfqBasket();
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState("");
  const [dueDate, setDueDate] = useState("");

  const submit = async () => {
    if (basket.items.length === 0) return;
    setSubmitting(true);
    try {
      const payload = {
        items: basket.items.map((x) => ({ product_id: x.product_id, qty: x.qty, notes: x.notes || undefined })),
        notes: notes || undefined,
        delivery_required_by: dueDate || undefined,
      };
      const { data } = await api.post("/school-rfqs", payload);
      toast.success(`RFQ ${data.rfq_number} submitted. Our team will get back with a quote shortly.`);
      basket.clear();
      close();
      onSubmitted && onSubmitted();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="rfq-drawer">
      <div className="flex-1 bg-black/40" onClick={close} />
      <aside className="w-full sm:w-[440px] h-full bg-white flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="font-heading font-semibold text-brand-navy">Your RFQ Basket</div>
          <button data-testid="btn-close-rfq" onClick={close}><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {basket.items.length === 0 ? (
            <div className="text-center text-slate-500 mt-10">Your RFQ basket is empty.</div>
          ) : basket.items.map((x) => (
            <div key={x.product_id} className="flex items-start gap-3 border-b border-slate-100 pb-3">
              <div className="flex-1">
                <div className="text-sm font-semibold text-brand-navy line-clamp-2">{x.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">{x.category}</div>
                <input
                  data-testid={`rfq-notes-${x.product_id}`}
                  placeholder="Specifications / notes (optional)"
                  value={x.notes || ""}
                  onChange={(e) => basket.setNotes(x.product_id, e.target.value)}
                  className="mt-2 w-full text-xs rounded-md border border-slate-200 py-1.5 px-2 focus:outline-none focus:border-brand-blue"
                />
              </div>
              <QtyStepper id={x.product_id} qty={x.qty} />
            </div>
          ))}
        </div>
        <div className="border-t border-slate-200 p-4 space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600">Required by</label>
            <input
              type="date"
              data-testid="rfq-due-date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full mt-1 rounded-md border border-slate-300 py-2 px-3 text-sm focus:outline-none focus:border-brand-blue"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Notes to SchoolLane team</label>
            <textarea
              data-testid="rfq-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Delivery address, payment terms, other context…"
              className="w-full mt-1 rounded-md border border-slate-300 py-2 px-3 text-sm focus:outline-none focus:border-brand-blue"
            />
          </div>
          <button
            data-testid="btn-submit-rfq"
            disabled={basket.items.length === 0 || submitting}
            onClick={submit}
            className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-md bg-brand-blue text-white font-semibold hover:bg-brand-blueDark disabled:bg-slate-300"
          >
            {submitting ? "Submitting…" : (<><Send size={14} /> Submit RFQ</>)}
          </button>
          <div className="text-[11px] text-slate-500 text-center">No commercial data is exchanged until a quote is issued.</div>
        </div>
      </aside>
    </div>
  );
}
